import {
  USERNAME_MIN_LENGTH,
  PREMIUM_USERNAME_MIN_LENGTH,
  PREMIUM_USERNAME_MAX_LENGTH,
  PREMIUM_USERNAME_PRICES,
  STANDARD_USERNAME_CHANGE_COINS,
} from '@/lib/premium-plans'

// =========================================================================
// USERNAME POLICY
// One place for every username rule, used by sign-up and by Edit Identity HUD:
//   1. Format            - letters, numbers, "_" and "." only, 1-20 characters.
//   2. Premium handles   - 1-4 characters are premium: bought once with REAL MONEY through Google Play (never coins).
//   3. Brand / trademark - names that use or imitate someone else's brand are refused with a
//                          "policy violation" message, so nobody can claim them.
//   4. Reserved names    - Animora's own / staff / system names.
// Only the name being CHOSEN is checked. Handles people already own are left alone.
// To block another brand, just add it to one of the lists below.
// =========================================================================

export const USERNAME_MAX_LENGTH = 20
export const USERNAME_FORMAT = /^[a-z0-9_.]+$/
export { USERNAME_MIN_LENGTH, PREMIUM_USERNAME_MIN_LENGTH, PREMIUM_USERNAME_MAX_LENGTH, PREMIUM_USERNAME_PRICES, STANDARD_USERNAME_CHANGE_COINS }

/** Shown when a username is refused because of a brand / trademark / copyright match. */
export const TRADEMARK_POLICY_MESSAGE =
  'Policy violation: this username uses a protected brand, trademark or copyrighted name (like Google, YouTube, Netflix). Animora does not allow usernames that use or imitate someone else\'s name or official account. Please choose a different username.'

export const RESERVED_POLICY_MESSAGE =
  'Policy violation: this username is reserved for Animora and its staff / system accounts. Please choose a different username.'

/** Reference price in RUPEES of a 1-4 character handle (0 = not a premium length). The real price comes from Google Play. */
export const premiumUsernamePrice = (name: string): number =>
  name.length >= PREMIUM_USERNAME_MIN_LENGTH && name.length <= PREMIUM_USERNAME_MAX_LENGTH ? (PREMIUM_USERNAME_PRICES[name.length] ?? 0) : 0

export const USERNAME_TOO_SHORT_MESSAGE = `Usernames must be at least ${USERNAME_MIN_LENGTH} character.`

// ---- 1) Brand / trademark / copyright names --------------------------------
// STRICT: blocked anywhere inside the name ("iloveyoutube", "google.support", "g00gle123").
// Only long, distinctive names go here so ordinary words are never caught by accident.
const BRANDS_STRICT = [
  // internet, social media, big tech
  'google', 'gmail', 'youtube', 'facebook', 'instagram', 'whatsapp', 'snapchat', 'tiktok', 'twitter', 'linkedin',
  'pinterest', 'spotify', 'netflix', 'primevideo', 'hotstar', 'jiocinema', 'disneyplus', 'hbomax', 'paramountplus',
  'microsoft', 'yahoo', 'openai', 'chatgpt', 'anthropic', 'deepseek', 'midjourney', 'github', 'stackoverflow',
  'wikipedia',
  // gaming
  'playstation', 'nintendo', 'xbox', 'minecraft', 'fortnite', 'roblox', 'valorant', 'leagueoflegends', 'callofduty',
  'genshinimpact', 'hoyoverse', 'mihoyo', 'clashofclans', 'brawlstars', 'pokemon', 'garena', 'epicgames',
  'riotgames', 'ubisoft', 'rockstargames',
  // phones, computers, cars, consumer brands
  'samsung', 'iphone', 'macbook', 'airpods', 'xiaomi', 'oneplus', 'nvidia', 'toyota', 'ferrari', 'lamborghini',
  'porsche', 'mercedesbenz', 'louisvuitton', 'adidas', 'gucci', 'rolex', 'cocacola', 'mcdonalds', 'starbucks',
  'alibaba', 'paypal', 'mastercard',
  // India: payments, shopping, telecom, banks
  'paytm', 'phonepe', 'googlepay', 'razorpay', 'flipkart', 'myntra', 'meesho', 'swiggy', 'zomato', 'airtel',
  'vodafone', 'hdfcbank', 'axisbank', 'icicibank', 'kotakbank', 'statebankofindia',
  // entertainment, anime & manga companies, piracy sites
  'disney', 'pixar', 'dccomics', 'warnerbros', 'crunchyroll', 'funimation', 'toeianimation', 'studioghibli', 'shueisha',
  'kodansha', 'shogakukan', 'ufotable', 'aniplex', 'kadokawa', 'bandainamco', 'witstudio', 'cloverworks', 'sanrio',
  'myanimelist', 'animeplanet', 'gogoanime', '9anime', 'kissanime', 'hianime', 'aniwatch', 'animixplay',
  // Animora itself
  'animora',
]

// EXACT: blocked when the name IS the brand (also with numbers on the end, look-alike letters, or an
// "official / support / team / india ..." style word around it), so ordinary words such as "apple pie",
// "marvelous", "niketa" or "intelligent" are not caught. Real names, common words and fan names stay allowed.
const BRANDS_EXACT = [
  // tech, platforms
  'apple', 'meta', 'amazon', 'oracle', 'windows', 'android', 'chrome', 'safari', 'bing', 'reddit', 'twitch', 'discord',
  'telegram', 'insta', 'gitlab', 'zoom', 'skype', 'slack', 'quora', 'tinder', 'bumble', 'hulu', 'hbo', 'ipad',
  'gpay', 'uber', 'ola', 'jio', 'tata', 'sbi', 'icici', 'hdfc', 'kotak', 'visa', 'freefire', 'pubg', 'bgmi',
  'reliance', 'blizzard', 'supercell',
  // brands
  'nike', 'puma', 'sony', 'intel', 'amd', 'ibm', 'dell', 'lenovo', 'redmi', 'poco', 'oppo', 'vivo', 'realme', 'honor',
  'tesla', 'bmw', 'audi', 'honda', 'yamaha', 'suzuki', 'mahindra', 'pepsi', 'kfc', 'dominos', 'chanel', 'prada',
  'panasonic', 'nokia', 'motorola',
  // entertainment & anime companies / franchises
  'marvel', 'toei', 'ghibli', 'mappa', 'bandai', 'madhouse', 'anilist', 'viz', 'sega', 'capcom', 'konami',
  'naruto', 'boruto', 'onepiece', 'dragonball', 'dragonballz', 'dragonballsuper', 'bleach', 'attackontitan',
  'shingekinokyojin', 'demonslayer', 'kimetsunoyaiba', 'jujutsukaisen', 'myheroacademia', 'bokunoheroacademia',
  'deathnote', 'fullmetalalchemist', 'hunterxhunter', 'tokyoghoul', 'sailormoon', 'evangelion', 'gundam',
  'swordartonline', 'spyxfamily', 'chainsawman', 'blackclover', 'fairytail', 'haikyuu', 'dragonquest',
  'finalfantasy', 'digimon', 'yugioh', 'doraemon', 'shinchan', 'hellokitty', 'sololeveling', 'onepunchman',
  'vinlandsaga', 'steinsgate', 'codegeass', 'cowboybebop', 'pikachu', 'mickeymouse', 'batman', 'superman',
  'spiderman', 'ironman', 'avengers', 'mario', 'zelda', 'genshin',
  // authority / impersonation
  'police', 'cyberpolice', 'cybercell', 'government', 'govt', 'ministry',
]

// ---- 2) Reserved for Animora ---------------------------------------------
const RESERVED = [
  'admin', 'administrator', 'moderator', 'mod', 'staff', 'support', 'help', 'helpdesk', 'security', 'system', 'root',
  'owner', 'founder', 'ceo', 'official', 'verified', 'team', 'service', 'contact', 'billing', 'payments', 'wallet',
  'coins', 'store', 'null', 'undefined', 'api', 'www',
]
// Note: "alonebeast" used to be reserved here because the admin was found by that username. The admin is now
// account-based (the Google account in ADMIN_EMAIL, enforced by isAdmin() in firestore.rules), so the name
// carries no power and anyone may use it.

// Words people add around a brand to look official ("googleofficial", "the_netflix", "youtube.support" ...).
const AFFIXES = [
  'official', 'real', 'the', 'its', 'iam', 'im', 'team', 'support', 'help', 'care', 'hq', 'india', 'indian', 'in',
  'app', 'apps', 'store', 'shop', 'studio', 'studios', 'media', 'music', 'tv', 'news', 'world', 'global', 'corp',
  'inc', 'ltd', 'llc', 'pvt', 'group', 'mod', 'admin', 'staff', 'verified', 'original', 'service', 'services',
  'customer', 'contact', 'anime', 'clips', 'us', 'uk',
].sort((a, b) => b.length - a.length)

const STRICT_LIST = Array.from(new Set(BRANDS_STRICT))
const EXACT_LIST = Array.from(new Set(BRANDS_EXACT))
const RESERVED_LIST = Array.from(new Set(RESERVED))
const RESERVED_SET = new Set(RESERVED_LIST)
const AFFIX_SET = new Set(AFFIXES)

const LEET: Record<string, string> = { '0': 'o', '3': 'e', '4': 'a', '5': 's', '7': 't', '8': 'b', '9': 'g' }

const collapse = (s: string) => s.replace(/(.)\1+/g, '$1')

/** Different spellings of the same name that could hide a brand: g00gle, YouTube_123, gooogle ... */
function variants(raw: string): string[] {
  const base = raw.toLowerCase().replace(/[^a-z0-9]/g, '')
  const leet = (s: string, one: 'i' | 'l') => s.replace(/[0-9]/g, (d) => (d === '1' ? one : (LEET[d] ?? d)))
  const noTrailingDigits = base.replace(/[0-9]+$/, '')
  const list = [
    base,
    noTrailingDigits,
    leet(base, 'i'),
    leet(base, 'l'),
    leet(noTrailingDigits, 'i'),
    leet(noTrailingDigits, 'l'),
  ].filter(Boolean)
  return Array.from(new Set(list.flatMap((v) => [v, collapse(v)])))
}

// true when `rest` is made only of "official / support / india ..." style words (or is empty)
function onlyAffixes(rest: string): boolean {
  if (!rest) return true
  if (AFFIX_SET.has(rest)) return true
  return AFFIXES.some((a) => rest.startsWith(a) && onlyAffixes(rest.slice(a.length)))
}

function matchesExactBrand(candidate: string, brand: string): boolean {
  if (candidate === brand) return true
  if (candidate.startsWith(brand) && onlyAffixes(candidate.slice(brand.length))) return true
  if (candidate.endsWith(brand) && onlyAffixes(candidate.slice(0, candidate.length - brand.length))) return true
  return false
}

export type UsernameCheck =
  | { ok: true; premiumPrice: number }
  | { ok: false; reason: 'format' | 'too-short' | 'trademark' | 'reserved'; message: string }

/** Every username rule in one call. `raw` may start with "@". */
export function checkUsername(raw: string): UsernameCheck {
  const name = raw.trim().replace(/^@/, '').toLowerCase()
  if (!name) return { ok: false, reason: 'format', message: 'Enter a username.' }
  if (name.length > USERNAME_MAX_LENGTH || !USERNAME_FORMAT.test(name)) {
    return { ok: false, reason: 'format', message: `Username can only use lowercase letters, numbers, "_" and "." (${USERNAME_MIN_LENGTH}-${USERNAME_MAX_LENGTH} characters).` }
  }
  if (name.length < USERNAME_MIN_LENGTH) return { ok: false, reason: 'too-short', message: USERNAME_TOO_SHORT_MESSAGE }

  const spellings = variants(name)
  const brandCollapsed = (b: string) => collapse(b)

  for (const s of spellings) {
    if (RESERVED_SET.has(s)) return { ok: false, reason: 'reserved', message: RESERVED_POLICY_MESSAGE }
    for (const r of RESERVED_LIST) {
      if (r.length >= 5 && matchesExactBrand(s, r)) return { ok: false, reason: 'reserved', message: RESERVED_POLICY_MESSAGE }
    }
  }
  for (const s of spellings) {
    for (const b of STRICT_LIST) {
      if (s.includes(b) || s.includes(brandCollapsed(b))) return { ok: false, reason: 'trademark', message: TRADEMARK_POLICY_MESSAGE }
    }
    for (const b of EXACT_LIST) {
      if (matchesExactBrand(s, b) || matchesExactBrand(s, brandCollapsed(b))) return { ok: false, reason: 'trademark', message: TRADEMARK_POLICY_MESSAGE }
    }
  }
  return { ok: true, premiumPrice: premiumUsernamePrice(name) }
}

export type UsernameCost = {
  coins: number // coins charged when saving (0 = nothing). Premium handles are NEVER paid with coins.
  rupees: number // premium handles only: reference price in rupees (paid through Google Play, see lib/billing-products.ts)
  kind: 'same' | 'free' | 'credit' | 'premium' | 'standard'
}

/**
 * What changing to `newName` costs, in the order that is best for the person:
 *  - premium (1-4 characters): real money through Google Play (kind 'premium', coins 0)
 *  - first change of a normal (6+) handle: free
 *  - an older paid change credit: uses it
 *  - otherwise: STANDARD_USERNAME_CHANGE_COINS
 * The admin account and capitalisation-only edits are always free.
 */
export function usernameChangeCost(
  currentName: string,
  newName: string,
  opts: { changeUsed: boolean; credits: number; isAdmin?: boolean },
): UsernameCost {
  const name = newName.trim().replace(/^@/, '')
  if (!name || name.toLowerCase() === currentName.toLowerCase()) return { coins: 0, rupees: 0, kind: 'same' }
  if (opts.isAdmin) return { coins: 0, rupees: 0, kind: 'same' }
  const premium = premiumUsernamePrice(name)
  if (premium > 0) return { coins: 0, rupees: premium, kind: 'premium' }
  if (!opts.changeUsed) return { coins: 0, rupees: 0, kind: 'free' }
  if (opts.credits > 0) return { coins: 0, rupees: 0, kind: 'credit' }
  return { coins: STANDARD_USERNAME_CHANGE_COINS, rupees: 0, kind: 'standard' }
}
